'use client';

/**
 * Pay-as-you-go minute packs on /pricing (TOS-52).
 *
 * One-time purchases for people who don't want a subscription (the exact ask
 * from churned Pro users: "hard to anticipate my usage on a monthly basis").
 * Signed-out clicks go through sign-up and resume via <AutoCheckout /> with
 * ?pack=…&checkout=1, the same round trip the plan cards use.
 *
 * Copy promises only what v1 delivers: minutes, no 30-minute file limit, and
 * the paid engine (speaker labels, timestamps) on the files the pack pays for.
 * Marketing surface, so legacy rgb(var(--…)) tokens per DESIGN.md.
 */

import { useState } from 'react';
import { useRouter } from 'next/navigation';

import { useSignedInHint } from '@/lib/auth/use-signed-in-hint';
import { startCheckout } from '@/lib/billing/start-checkout';
import { PAYG_PACKS, type PaygPackSize } from '@/lib/billing/payg-catalog';

export function PaygPacks() {
  const isSignedIn = useSignedInHint();
  const router = useRouter();
  const [loading, setLoading] = useState<PaygPackSize | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const buy = async (size: PaygPackSize) => {
    setMessage(null);
    const from = new URLSearchParams(window.location.search).get('from') ?? 'payg_row';

    if (!isSignedIn) {
      const params = new URLSearchParams({ pack: size, checkout: '1', from });
      router.push(`/sign-up?redirect_url=${encodeURIComponent(`/pricing?${params}`)}`);

      return;
    }

    setLoading(size);

    const result = await startCheckout({
      plan: `payg_${size}`,
      interval: 'monthly',
      pack: size,
      from,
      page: window.location.pathname,
    });

    if (result.status === 'already_subscribed') {
      setMessage('Minute packs are for accounts without a subscription. Your plan already includes monthly minutes.');
    } else if (result.status === 'error') {
      setMessage(result.message);
    }

    if (result.status !== 'redirecting') setLoading(null);
  };

  return (
    <div id="payg" className="mt-16 scroll-mt-24">
      <div className="text-center mb-6">
        <h3 className="text-xl md:text-2xl font-semibold text-[rgb(var(--foreground))]">
          Rather pay as you go?
        </h3>
        <p className="mt-2 text-sm md:text-base text-[rgb(var(--foreground-secondary))]">
          Buy minutes once. No subscription, nothing renews. Minutes stay on your account for 12 months.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 max-w-3xl mx-auto">
        {PAYG_PACKS.map((p) => (
          <div
            key={p.size}
            className="rounded-2xl border border-[rgb(var(--surface-border))] bg-[rgb(var(--surface))] p-5 flex flex-col"
          >
            <p className="text-sm text-[rgb(var(--foreground-secondary))]">{p.name}</p>
            <p className="mt-1 text-2xl font-semibold text-[rgb(var(--foreground))]">{p.minutes} minutes</p>
            <p className="mt-1 text-[rgb(var(--foreground))]">
              ${p.priceUsd} <span className="text-sm text-[rgb(var(--foreground-secondary))]">one time</span>
            </p>
            <button
              type="button"
              onClick={() => void buy(p.size)}
              disabled={loading !== null}
              className="mt-4 min-h-[44px] rounded-lg border border-[rgb(var(--primary))] px-4 py-2 text-sm font-medium text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10 disabled:opacity-60"
            >
              {loading === p.size ? 'Opening checkout…' : `Buy ${p.minutes} minutes`}
            </button>
          </div>
        ))}
      </div>

      <p className="mt-4 text-center text-xs text-[rgb(var(--foreground-secondary))]">
        Pack minutes kick in after your free daily minutes, with no 30-minute file limit. Files they pay for get
        speaker labels and timestamps.
      </p>

      {message && (
        <p role="alert" className="mt-4 text-center text-sm text-[rgb(var(--foreground))]">
          {message}
        </p>
      )}
    </div>
  );
}
